import {
  GSCaptchaCheckbox,
  defaultLayouts,
  defaultGapping,
  defaultTypography,
  golfstatusTypography,
  GSButton,
  golfstatusColors,
  useMediaQuery,
} from "golfstatus_react_components";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCaptchaData,
  setCaptchaToken,
} from "../../reducers/invoiceSlice";

import {
  clearTokens,
  calculatePricing,
  selectLoading,
  selectRegistrationCustomer,
  selectRegistrationOrder,
  payOrderThunk,
} from "../../reducers/registrationOrderSlice"
import {
  CUSTOMER_INVOICE,
  getPaymentMethods,
  isBankAttached,
  isCardAttached,
  PAY_AT_COURSE,
} from "../../forms/CartSections";
import { useNotificationAction, useNotifications } from "../../hooks/notificationHooks";
import { REJECTED } from "../../app/api";
import { sponsorIsValid } from "../../models/Registration";

const PaymentWidget = () => {
  const dispatch = useDispatch();
  const captachData = useSelector(selectCaptchaData);
  const captchaRef = useRef(null);

  const notifications = useNotifications().orderNotifications;

  const registrationOrder = useSelector(selectRegistrationOrder);
  const customer = useSelector(selectRegistrationCustomer);

  const loading = useSelector(selectLoading);
  const paying = loading?.filter((l) => l === "payOrderThunk");
  const calculating = loading?.filter((l) => l === "calculatePricing")?.length > 0;
  const calculationFailed =
    notifications?.filter?.(
      (n) => n.result === "rejected" && n.id === "calculatePricing"
    )?.length > 0;

  const currentPaymentMethod = getPaymentMethods().find?.(
    (m) => m?.type === registrationOrder?.paymentType
  );

  const [showCaptcha] = useState(true);

  const totalCostWithFees =
    registrationOrder?.totalCostWithFees !== ""
      ? registrationOrder?.totalCostWithFees
      : "0.0";
  const freeRegistration = totalCostWithFees === "0.0";

  const { isPhone } = useMediaQuery();

  const fail = useCallback(() => {
    if (showCaptcha && typeof grecaptcha !== 'undefined') {
      grecaptcha.enterprise.reset(); // eslint-disable-line no-undef
    }
    
    dispatch(clearTokens());
  }, [dispatch, showCaptcha]);

  useNotificationAction(REJECTED, notifications, fail, "payOrderThunk");

  const paymentAttached = () => {
    if (freeRegistration) {
      return true;
    }
    const cardAttached = isCardAttached(registrationOrder);
    const bankAttacted = isBankAttached(registrationOrder);
    const payLater =
      registrationOrder.paymentType === PAY_AT_COURSE ||
      registrationOrder.paymentType === CUSTOMER_INVOICE;
    return cardAttached || bankAttacted || payLater;
  };

  const hasInvalidSponsors = () => {
    return registrationOrder?.registrationOrderItems?.some((item) =>
      item?.inProgressSponsors?.some((sponsor) => !sponsorIsValid(sponsor))
    );
  };

  //for right now use a memo to prevent re render
  const captcha = useMemo(() => {
    if (!showCaptcha || (customer?.firstName ?? "") === "") {
      return null;
    }

    return (
      <div ref={captchaRef}>
        <GSCaptchaCheckbox
          captchaKey={process.env.REACT_APP_CHECKBOX_CAPTCHA_KEY}
          action="CHECKOUT"
          setToken={(value) => {
            dispatch(setCaptchaToken(value));
          }}
        />
      </div>
    );
  }, [dispatch, showCaptcha, customer?.firstName]);
  //----------------------------------------

  const layout = isPhone()
    ? defaultLayouts.vStack
    : defaultLayouts.hStack.align("flex-start", "center");

  const pay = () => {
    const paySettings = {
      registrationOrder,
      registrationCustomer: customer,
    };
    paySettings["captcha_data"] = captachData;
    if (showCaptcha) {
      dispatch(payOrderThunk(
        { captcha_data: captachData, registrationOrderId: registrationOrder.id, saveCard: registrationOrder.saveCard }
      ))
    } else {
      dispatch(payOrderThunk(
        { registrationOrderId: registrationOrder.id, saveCard: registrationOrder.saveCard }
      ))
      //dispatch(completeOrder()); 
      //navigate("receipt")
    }

    if (showCaptcha) {
      // clear captcha
      dispatch(setCaptchaToken(""));
    }
  };

  const buttonLabel = () => {
    if ([1, 2].includes(currentPaymentMethod?.id)) {
      return "Pay Now";
    } else {
      return "Confirm Order";
    }
  };

  const getTOS = () => {
    if ([1, 2].includes(currentPaymentMethod?.id)) {
      return (
        <div>
          <span style={{ ...defaultTypography.bodyRegular.withOpacity() }}>
            {`By tapping "${buttonLabel()}" you authorize payment and agree to
          GolfStatus' `}
          </span>{" "}
          <a
            href="https://golfstatus.com/golfstatus-terms-of-service"
            target="_blank"
            rel="noreferrer"
            style={{
              color: golfstatusColors.cyan700,
              fontWeight: golfstatusTypography.boldFontWeight,
              textDecoration: "underline",
            }}
          >
            Terms of Use
          </a>{" "}
          <span style={{ ...defaultTypography.bodyRegular.withOpacity() }}>
            {"and"}
          </span>{" "}
          <a
            href="https://golfstatus.com/golfstatus-privacy-policy"
            target="_blank"
            rel="noreferrer"
            style={{
              color: golfstatusColors.cyan700,
              fontWeight: golfstatusTypography.boldFontWeight,
              textDecoration: "underline",
            }}
          >
            Privacy Policy
          </a>
        </div>
      );
    } else {
      return (
        <div>
          <span style={{ ...defaultTypography.bodyRegular.withOpacity() }}>
            {`By tapping "${buttonLabel()}" you agree to
          GolfStatus'`}
          </span>{" "}
          <a
            href="https://golfstatus.com/golfstatus-terms-of-service"
            target="_blank"
            rel="noreferrer"
            style={{
              color: golfstatusColors.cyan700,
              fontWeight: golfstatusTypography.boldFontWeight,
              textDecoration: "underline",
            }}
          >
            Terms of Use
          </a>{" "}
          <span style={{ ...defaultTypography.bodyRegular.withOpacity() }}>
            {"and"}
          </span>{" "}
          <a
            href="https://golfstatus.com/golfstatus-privacy-policy"
            target="_blank"
            rel="noreferrer"
            style={{
              color: golfstatusColors.cyan700,
              fontWeight: golfstatusTypography.boldFontWeight,
              textDecoration: "underline",
            }}
          >
            Privacy Policy
          </a>
        </div>
      );
    }
  };

  const getCaptchaOpacity = () => {
    if (paying.length > 0) {
      return 0;
    }
    if (calculating) {
      return 0;
    }
    if (calculationFailed) {
      return 0;
    }
    if ((customer?.firstName ?? "") === "") {
      return 0;
    }
    if (!paymentAttached()) {
      return 0;
    }
    if (hasInvalidSponsors()) {
      return 0;
    }
    return 1;
  };

  const getButton = () => {
    if (paying.length > 0) {
      return (
        <GSButton
          title="Processing..."
          type="grey"
          style={{ flexShrink: "0" }}
          isDisabled={true}
        />
      );
    }
    if (calculating) {
      return (
        <GSButton
          title="calculating final price..."
          type="grey"
          style={{ flexShrink: "0" }}
          isDisabled={true}
        />
      );
    }
    if (calculationFailed) {
      return (
        <GSButton
          title="calculate price"
          type="grey"
          style={{ flexShrink: "0" }}
          onClick={() => {
            dispatch(calculatePricing(registrationOrder));
          }}
        />
      );
    }
    return (
      <GSButton
        title={buttonLabel()}
        type="black"
        style={{ flexShrink: "0" }}
        isDisabled={
          ((captachData?.captcha_token ?? "") === "" && showCaptcha) ||
          !paymentAttached() ||
          hasInvalidSponsors()
        }
        onClick={() => {
          pay();
        }}
      />
    );
  };

  return (
    <div
      style={{
        ...defaultLayouts.vStack,
        ...defaultGapping.xLargeGap,
        ...defaultLayouts.fullWidth,
      }}
    >
      <div
        style={{
          ...defaultLayouts.fullWidth,
          ...layout,
          ...defaultGapping.largeGap,
        }}
      >
        <div
          style={{
            ...defaultLayouts.fullWidth,
            ...defaultLayouts.vStack.align("flex-start", "center"),
            ...defaultGapping.largeGap,
          }}
        >
          <div style={{ ...defaultTypography.bodyRegular.withOpacity() }}>
            Tap “{buttonLabel()}” to finalize this order.
          </div>
          <div style={{ opacity: getCaptchaOpacity(), transition: ".3s" }}>
            {captcha}
          </div>
        </div>

        {getButton()}
      </div>
      {getTOS()}
    </div>
  );
};

export default PaymentWidget;
